
import React from "react";
import { UserCheck, MapPin, Star } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import type { MentorData } from "@/utils/mentorUtils";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";

interface MentorCardProps {
  mentor: MentorData;
  onViewProfile: (mentorId: string) => void;
}

const MentorCard: React.FC<MentorCardProps> = ({ mentor, onViewProfile }) => {
  const maxVisibleTags = 5;
  const hasMoreTags = mentor.specialties.length > maxVisibleTags;
  const visibleTags = mentor.specialties.slice(0, maxVisibleTags);
  const remainingTags = mentor.specialties.length - maxVisibleTags;

  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow min-h-[400px] flex flex-col">
      <CardContent className="p-0 flex flex-col h-full">
        <div className="p-6 flex-grow flex flex-col">
          <div className="flex items-start gap-4">
            <Avatar className="w-20 h-20 border-2 border-emu-blue">
              <AvatarFallback>{mentor.name.charAt(0)}</AvatarFallback>
              <AvatarImage src={mentor.avatar} alt={mentor.name} />
            </Avatar>
            <div>
              <h3 className="font-semibold text-lg">{mentor.name}</h3>
              <div className="flex items-center text-sm text-gray-600 mb-1">
                <UserCheck size={16} className="mr-1" />
                {mentor.profession}
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <MapPin size={16} className="mr-1" />
                {mentor.location}
              </div>
            </div>
          </div>
          
          <div className="mt-4 flex-grow">
            <p className="text-gray-600 text-sm line-clamp-2">
              {mentor.bio}
            </p>
          </div>
          
          <div className="flex flex-wrap gap-2 mt-4">
            {visibleTags.map(specialty => (
              <Badge 
                key={specialty} 
                variant="outline"
                className="bg-emu-blue/10 text-emu-blue border-emu-blue/30 hover:bg-emu-blue/20 text-xs"
              >
                {specialty}
              </Badge>
            ))}
            {hasMoreTags && (
              <HoverCard>
                <HoverCardTrigger asChild>
                  <Badge 
                    variant="outline"
                    className="bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200 cursor-pointer text-xs"
                  >
                    +{remainingTags} more
                  </Badge>
                </HoverCardTrigger>
                <HoverCardContent className="w-64">
                  <div className="flex flex-wrap gap-2">
                    {mentor.specialties.slice(maxVisibleTags).map(specialty => (
                      <Badge key={specialty} variant="outline" className="text-xs">
                        {specialty}
                      </Badge>
                    ))}
                  </div>
                </HoverCardContent>
              </HoverCard>
            )}
          </div>
        </div>
        
        <div className="grid grid-cols-2 border-t">
          <div className="p-4 text-center border-r">
            <div className="text-sm text-gray-500">Rating</div>
            <div className="font-semibold text-emu-blue flex items-center justify-center">
              {mentor.rating ? (
                <>
                  {mentor.rating}/5
                  <Star size={14} className="text-yellow-400 ml-1 fill-yellow-400" />
                </>
              ) : (
                <span className="text-gray-400 text-sm">No ratings</span>
              )}
            </div>
          </div>
          <div className="p-4 text-center">
            <div className="text-sm text-gray-500">Reviews</div>
            <div className="font-semibold text-emu-blue">
              {mentor.reviews === 0 ? (
                <span className="text-gray-400 text-sm">No reviews</span>
              ) : (
                mentor.reviews
              )}
            </div>
          </div>
        </div>
        
        <div className="p-4 bg-gray-50 border-t">
          <Button 
            className="w-full bg-emu-blue hover:bg-emu-blue/90"
            onClick={() => onViewProfile(mentor.id)}
          >
            View Profile
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default MentorCard;
